import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@api'
import { appendAvatarToFormData } from '@utils'
import { useApiErrorHandler } from '../../../useApiErrorHandler'

export interface ScanBusinessCardResponse {
  ghostProfileId: string
  parsedData: {
    firstName: string | null
    lastName: string | null
    email: string | null
    phone: string | null
    company: string | null
    jobTitle: string | null
    website: string | null
    linkedin: string | null
    address: string | null
  }
  imageUrl: string
  avatarUrl: string | null
}

const scanBusinessCard = async (imageUri: string): Promise<ScanBusinessCardResponse> => {
  const formData = new FormData()

  // fetch(data:...) is blocked by our CSP (connect-src has no data:), and
  // native camera URIs (file://) must use the RN {uri, type, name} shape.
  // appendAvatarToFormData handles both platforms correctly.
  appendAvatarToFormData(formData, imageUri, 'image', 'business-card.jpg')

  const { data } = await api.post<ScanBusinessCardResponse>('/ft_business-card-scan', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })

  return data
}

export const useScanBusinessCard = () => {
  const queryClient = useQueryClient()
  const handleApiError = useApiErrorHandler()

  return useMutation<ScanBusinessCardResponse, Error, string>({
    mutationFn: scanBusinessCard,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['contacts'] })
    },
    // A card scan also creates a contact (and can create a ghost profile), so
    // a free user may hit PLAN_LIMIT_CONTACTS_REACHED or
    // PLAN_LIMIT_GHOST_PROFILES_REACHED (402) → paywall, not a raw toast.
    onError: handleApiError,
  })
}
